import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { ConfirmActionModal } from '../surveys/results/ConfirmActionModal';
import { useCurrentUser } from '../auth';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SelectField } from '../../components/ui/SelectField';
import { Spinner } from '../../components/ui/Spinner';
import { availabilityErrors } from '../../lib/appointments/availability';
import type { BookingAvailabilityWindow } from '../../types/appointments';
import { AvailabilityEditor } from './AvailabilityEditor';
import { AvailabilityPreview } from './AvailabilityPreview';
import {
  BookingRulesEditor,
  ruleDraftErrors,
  ruleDraftFrom,
  rulesFromDraft,
  type RuleDraft,
} from './BookingRulesEditor';
import { StaffSection } from './StaffSection';
import { AfCalendarFields } from './AfCalendarFields';
import { afClubError, afConfigFromDraft, afDraftFrom, emptyAfDraft, type AfFormDraft } from '../../lib/appointments/afCalendarForm';
import { BOOKING_TIMEZONES } from './timezones';
import { archiveBookingCalendar, getBookingCalendar, getCalendarArchiveCheck, getCalendarStatus, updateBookingCalendar, type ArchiveCheck } from './api';

// One booking calendar. Sections rather than GHL's tabs — Basic details · Staff ·
// Availability · Booking rules · What it would offer — because the section-based
// detail page is what the rest of this app does and tabs hide state.
//
// There is no shared `Section` primitive in components/ui (AccountDetail and
// BotSettingsPage each compose one from Card), so this does the same rather than
// inventing a third heading treatment.
function Section({
  title,
  children,
  description,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

export function BookingCalendarPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const [archiveInfo, setArchiveInfo] = useState<ArchiveCheck | null>(null);
  const openArchive = useMutation({
    mutationFn: () => getCalendarArchiveCheck(id),
    onSuccess: setArchiveInfo,
    onError: (err: Error) => toast.error(err.message),
  });
  const archive = useMutation({
    mutationFn: () => archiveBookingCalendar(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['booking-calendars'] });
      toast.success('Calendar archived.');
      navigate('/booking-calendars');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['booking-calendar', id],
    queryFn: () => getBookingCalendar(id),
    enabled: id !== '',
  });
  const { data: status } = useQuery({ queryKey: ['appointments-status'], queryFn: getCalendarStatus });

  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [availability, setAvailability] = useState<BookingAvailabilityWindow[]>([]);
  const [ruleDraft, setRuleDraft] = useState<RuleDraft | null>(null);
  const [af, setAf] = useState<AfFormDraft>(emptyAfDraft());

  // Seeded from the server once the calendar arrives. ⚠️ Keyed on the calendar's
  // `updatedAt` so a successful save re-seeds the form from what was actually
  // stored, and an edit in another tab is not silently overwritten by a stale
  // draft.
  const stamp = data?.calendar.updatedAt;
  useEffect(() => {
    if (!data) return;
    setName(data.calendar.name);
    setTimezone(data.calendar.timezone);
    setEnabled(data.calendar.enabled);
    setAvailability(data.calendar.availability);
    setRuleDraft(ruleDraftFrom(data.calendar.rules));
    setAf(data.calendar.anytimeFitness ? afDraftFrom(data.calendar.anytimeFitness) : emptyAfDraft());
  }, [data?.calendar.id, stamp]);

  const save = useMutation({
    mutationFn: () => {
      if (!ruleDraft) throw new Error('Not loaded yet.');
      return updateBookingCalendar(id, {
        name: name.trim(),
        timezone,
        enabled,
        availability,
        rules: rulesFromDraft(ruleDraft),
        ...(data?.calendar.anytimeFitness ? { anytimeFitness: afConfigFromDraft(af) } : {}),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['booking-calendar', id] });
      void queryClient.invalidateQueries({ queryKey: ['booking-calendars'] });
      // ⚠️ The rules change what gets offered, so the preview has to be re-read
      // rather than left showing the old policy's answer.
      void queryClient.invalidateQueries({ queryKey: ['booking-availability', id] });
      toast.success('Calendar saved.');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isLoading || !data || !ruleDraft) return <Spinner />;

  const ruleErrors = ruleDraftErrors(ruleDraft);
  const windowErrors = availabilityErrors(availability);
  const isAf = Boolean(data.calendar.anytimeFitness);
  const canSave =
    (!isAf || afClubError(af.locationNumber) === null) &&
    name.trim() !== '' &&
    timezone !== '' &&
    Object.keys(ruleErrors).length === 0 &&
    windowErrors.length === 0;

  return (
    <div>
      <PageHeader
        title={data.calendar.name}
        description={data.calendar.accountName ?? undefined}
        backHref="/booking-calendars"
        actions={
          <div className="flex gap-2">
            {user.role === 'admin' && !data.calendar.archivedAt && (
              <Button variant="outline" disabled={openArchive.isPending} onClick={() => openArchive.mutate()}>
                Archive calendar
              </Button>
            )}
            <Button disabled={!canSave || save.isPending} onClick={() => save.mutate()}>
              {save.isPending && <Spinner className="mr-1 h-4 w-4" />}
              Save changes
            </Button>
          </div>
        }
      />

      {data.calendar.archivedAt && (
        <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          This calendar is archived. No bot offers times from it.
        </div>
      )}

      {archiveInfo && (
        <ConfirmActionModal
          title={`Archive "${data.calendar.name}"?`}
          body={
            archiveInfo.bots.length > 0 ? (
              <p>
                This calendar is in use by {archiveInfo.bots.map((b) => `"${b.botName}"`).join(', ')}. Choose another
                calendar in the bot&apos;s settings first, then archive this one.
              </p>
            ) : (
              <>
                <p>It will be hidden from the list and no bot will offer times from it. Nothing is deleted.</p>
                {archiveInfo.futureAppointments > 0 && (
                  <p className="mt-2">
                    It has {archiveInfo.futureAppointments} future appointment{archiveInfo.futureAppointments === 1 ? '' : 's'}.
                    They stay booked in the provider and visible in conversations.
                  </p>
                )}
              </>
            )
          }
          confirmLabel={archiveInfo.bots.length > 0 ? 'Close' : 'Archive calendar'}
          destructive={archiveInfo.bots.length === 0}
          busy={archive.isPending}
          onConfirm={() => (archiveInfo.bots.length > 0 ? setArchiveInfo(null) : archive.mutate())}
          onCancel={() => setArchiveInfo(null)}
        />
      )}

      <div className="space-y-6">
        <Section title="Basic details">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="calendar-name">Name</Label>
              <Input
                id="calendar-name"
                className="mt-1"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                What this calendar books — a test drive, a service handover.
              </p>
            </div>
            <div>
              <Label htmlFor="calendar-timezone">Timezone</Label>
              <SelectField
                value={timezone}
                onValueChange={setTimezone}
                options={BOOKING_TIMEZONES}
                id="calendar-timezone"
                className="mt-1"
                aria-label="Calendar timezone"
              />
              {/* ⚠️ Not cosmetic. Every time offered and every confirmation sent
                  is worked out in this zone, and Queensland does not observe
                  daylight saving while NSW, VIC and NZ do. */}
              <p className="mt-1 text-xs text-muted-foreground">
                The client&apos;s local time, not ours. Every time we offer and every confirmation
                we send is in this zone.
              </p>
            </div>
          </div>

          <label className="mt-4 flex items-center gap-2 text-sm">
            <Checkbox
              checked={enabled}
              onCheckedChange={(checked) => setEnabled(checked === true)}
              aria-label="Calendar enabled"
            />
            Available for booking
          </label>
          {!enabled && (
            <p className="mt-1 text-xs text-amber-700">
              Turned off: nothing will be offered from this calendar.
            </p>
          )}
        </Section>

        {isAf ? (
          <Section
            title="Anytime Fitness club calendar"
            description="Times are offered from the club's own calendar and a booking goes straight into it. The club's name and time zone come from its Business Profile, and the opening hours and staff are Anytime Fitness's, not ours."
          >
            <AfCalendarFields value={af} onChange={setAf} accountId={data.calendar.accountId} />
          </Section>
        ) : (
          <>
            <Section title="Staff">
              <StaffSection
                calendarId={id}
                members={data.members}
                serviceAccountEmail={status?.serviceAccountEmail ?? null}
                calendarConfigured={status?.calendarConfigured ?? false}
              />
            </Section>

            <Section title="Availability">
              <AvailabilityEditor value={availability} onChange={setAvailability} timezone={timezone} />
            </Section>
          </>
        )}

        <Section title="Booking rules">
          <BookingRulesEditor value={ruleDraft} onChange={setRuleDraft} />
        </Section>

        <Section
          title="What this calendar would offer"
          description="A live check against every connected calendar, run through the same rules the bot will use."
        >
          {/* ⚠️ Reflects the SAVED calendar, not the unsaved form — it is a real
              read of what a customer would be told right now, so pretending it
              already includes an unsaved change would be a lie about a live
              system. */}
          {save.isPending || !canSave ? (
            <p className="text-sm text-muted-foreground">
              Save your changes to see what they would offer.
            </p>
          ) : (
            <AvailabilityPreview calendarId={id} />
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            Showing the saved settings.{' '}
            {data.calendar.enabled ? null : <Badge variant="neutral">Calendar turned off</Badge>}
          </p>
        </Section>
      </div>
    </div>
  );
}
